import React, { useEffect, useMemo, useState } from 'react';
import { api, fmtDay, daysLeft, STATUS_WORDS, CLOSED, cmsSection, cmsSubsection } from './api.js';
import { Card, Button, ItemBadge, StatusPill, KindPill, inputCls, Empty } from './ui.jsx';
import ExportModal from './ExportModal.jsx';

export default function ItemsTab({ boot, people, filter, onFilter, onOpen, refreshKey, initialItems = null }) {
  const [items, setItems] = useState(initialItems); // initialItems: the render harness only
  const [q, setQ] = useState(filter?.q || '');
  const [exporting, setExporting] = useState(false);
  const cycleId = boot.cycle?.id;
  const f = useMemo(() => filter || {}, [filter]);

  useEffect(() => {
    if (!cycleId || initialItems) return;
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v) qs.set(k, v);
    let alive = true;
    api(`/cycles/${cycleId}/items?${qs.toString()}`).then((d) => { if (alive) setItems(d?.success ? d.items : []); });
    return () => { alive = false; };
  }, [cycleId, f, refreshKey, initialItems]);

  if (!cycleId) return <Card><Empty>Open the year first (Overview).</Empty></Card>;
  const set = (k, v) => onFilter({ ...f, [k]: v || undefined });
  const topicsOf = (letter) => (boot.edition.topics || []).filter((t) => t.section_letter === letter);

  return (
    <Card title={`Items${items ? ` · ${items.length}` : ''}`} right={<span className="flex items-center gap-3"><span className="text-xs text-slate-500 hidden md:inline">CMS order. Click a row to open the item.</span><Button kind="secondary" onClick={() => setExporting(true)} title="Excel in the plan's format with the logo: the whole year, one department or person, or one CMS section.">Export to Excel…</Button></span>}>
      <ExportModal boot={boot} people={people} open={exporting} onClose={() => setExporting(false)} current={f} />
      <div className="flex flex-wrap gap-2 mb-4">
        <select className={`${inputCls} w-auto`} value={f.section || ''} onChange={(e) => { onFilter({ ...f, section: e.target.value || undefined, topic: undefined }); }}>
          <option value="">All sections</option>
          {boot.edition.sections.map((s) => <option key={s.letter} value={s.letter}>{s.letter}. {s.title}</option>)}
        </select>
        {f.section && (
          <select className={`${inputCls} w-auto`} value={f.topic || ''} onChange={(e) => set('topic', e.target.value)}>
            <option value="">All subsections</option>
            {topicsOf(f.section).map((t) => <option key={t.key} value={t.key}>{t.roman}. {t.cms_title || t.title}</option>)}
          </select>
        )}
        <select className={`${inputCls} w-auto`} value={f.status || ''} onChange={(e) => set('status', e.target.value)}>
          <option value="">Any status</option>
          <option value="open">Open</option>
          <option value="overdue">Overdue</option>
          {Object.entries(STATUS_WORDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className={`${inputCls} w-auto`} value={f.department || ''} onChange={(e) => set('department', e.target.value)}>
          <option value="">Any department</option>
          {(people?.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select className={`${inputCls} w-auto`} value={f.owner || ''} onChange={(e) => set('owner', e.target.value)}>
          <option value="">Any person</option>
          {(people?.users || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          <option value="none">No one accountable</option>
        </select>
        <select className={`${inputCls} w-auto`} value={f.applicable || ''} onChange={(e) => set('applicable', e.target.value)}>
          <option value="">Applies and not</option>
          <option value="yes">Applies to this plan</option>
          <option value="no">Does not apply</option>
        </select>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); set('q', q.trim()); }}>
          <input className={`${inputCls} w-56`} placeholder="Search words, or a CMS ref (H.III.1)" value={q} onChange={(e) => setQ(e.target.value)} />
        </form>
        {Object.values(f).some(Boolean) && <button type="button" className="text-xs text-slate-500 underline" onClick={() => { setQ(''); onFilter({}); }}>Clear</button>}
      </div>

      {items === null ? <Empty>Loading…</Empty> : items.length === 0 ? <Empty>No items match.</Empty> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="py-2 pr-3 font-semibold">CMS ref</th><th className="py-2 pr-3 font-semibold">What CMS asks</th><th className="py-2 pr-3 font-semibold">Answer</th>
              <th className="py-2 pr-3 font-semibold">Department</th><th className="py-2 pr-3 font-semibold">Due</th><th className="py-2 pr-3 font-semibold">Status</th>
            </tr></thead>
            <tbody>
              {items.map((it) => {
                const left = daysLeft(it.due_date);
                const late = !CLOSED.has(it.status) && it.applicable && left !== null && left < 0;
                const depts = Array.isArray(it.department_names) && it.department_names.length ? it.department_names : (it.department_name ? [it.department_name] : []);
                return (
                  <tr key={it.id} className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${!it.applicable ? 'opacity-60' : ''}`} onClick={() => onOpen(it.id)}>
                    <td className="py-2.5 pr-3 align-top"><ItemBadge cmsRef={it.cms_ref} seq={it.seq} /></td>
                    <td className="py-2.5 pr-3 align-top max-w-[460px]">
                      <div className="text-xs text-slate-500">{cmsSection(it)} · {cmsSubsection(it)}</div>
                      <div className="text-slate-800 mt-0.5 line-clamp-2">{it.plain_requirement || it.cms_text}</div>
                      {(it.evidence_count > 0 || it.policy_proposals > 0 || it.comments?.count > 0) && (
                        <div className="text-[11px] text-slate-500 mt-1 flex gap-2 flex-wrap">
                          {it.evidence_count > 0 && <span>{it.evidence_count} file{it.evidence_count === 1 ? '' : 's'}</span>}
                          {it.policy_proposals > 0 && <span>{it.policy_proposals} policy proposal{it.policy_proposals === 1 ? '' : 's'}</span>}
                          {it.comments?.count > 0 && <span className={it.comments.unread ? 'text-orange-700 font-semibold' : ''}>{it.comments.count} comment{it.comments.count === 1 ? '' : 's'}{it.comments.unread ? ` · ${it.comments.unread} new` : ''}</span>}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 pr-3 align-top"><KindPill kind={it.kind} /></td>
                    <td className="py-2.5 pr-3 align-top text-slate-700">{depts.length ? depts.join(', ') : <span className="text-rose-700">No department</span>}<div className="text-xs text-slate-500">{(it.accountable_names || []).length ? it.accountable_names.join(', ') : it.owner_name || 'no one accountable'}{(it.assignee_names || []).length > (it.accountable_names || []).length ? ` + ${it.assignee_names.length - Math.max(1, (it.accountable_names || []).length)} more` : ''}</div></td>
                    <td className={`py-2.5 pr-3 align-top whitespace-nowrap ${late ? 'text-rose-700 font-semibold' : 'text-slate-600'}`}>{fmtDay(it.due_date)}{late ? ` · ${-left}d late` : ''}</td>
                    <td className="py-2.5 pr-3 align-top"><StatusPill status={it.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* CMS ref badges (H.III.1) follow the app theme; sections and subsections carry CMS's own names. */}
    </Card>
  );
}
